/* eslint-disable @typescript-eslint/ban-ts-comment */
import { canonicalNodeNumber } from './node-number';
import {
  Tenancy_Node_Type_Enum_Ps_Enum,
  Tenancy_Rich_Asset_Type_Enum_Ps_Enum,
} from '@pypestream/api-services';

import {
  TableNode,
  NodeColumns,
  columnOrder,
  defaultRowData,
  tableStore,
} from '..';
import { escapeJsonValues } from '../../components/table/utils';
import { excludeGroupNodes } from './exclude-group-nodes';

/** Properties required by pif-compiler - never remove */
const REQUIRED_NODE_KEYS = ['nodeNumber', 'nodeType'];

/** Decision-only properties - remove from action nodes */
const DECISION_ONLY_PROPERTIES = [
  'message',
  'richAsset',
  'behaviors',
  'answerRequired',
  'nluDisabled',
  'nextNodes',
  'cssClassName',
];

/** Action-only properties - remove from decision nodes */
const ACTION_ONLY_PROPERTIES = [
  'command',
  'description',
  'output',
  'nodeInput',
  'parameterInput',
  'decisionVariable',
  'whatsNext',
];

/**
 * Joins an array of strings/numbers into a delimited string for the agent compiler.
 * Pass-through: each entry is stringified and joined verbatim — no trim, no flatten,
 * no empty-token filtering. The compiler receives exactly what the user typed in the
 * cell (the cell stores `raw.split(separator)`, so a round-trip join reproduces the
 * input character-for-character). Null/undefined entries become empty strings.
 */
export const joinDelimitedField = (
  // A non-array is a legal input, not a type escape: see PSTUDIO-4081 below.
  value:
    | ReadonlyArray<string | number | null | undefined>
    | string
    | number
    | undefined,
  delimiter: ',' | '|'
): string => {
  if (!value || (Array.isArray(value) && value.length === 0)) return '';
  // PSTUDIO-4081: the row can be holding the RAW typed text of an open panel
  // edit — PSTUDIO-3882 writes that draft into the row per keystroke and
  // canonicalizes it only on commit. A string passes the emptiness gate above
  // (its `length` is a character count) and then died on `.map`, which
  // `validate()` swallows into a `console.error` — so one uncommitted cell
  // silently stopped the whole solution producing PIF errors. Such a value is
  // already the joined form the compiler wants, so pass it through.
  //
  // Only the shapes an editor can leave behind, mirroring what
  // `coerceRowArrayColumns` is willing to convert: `String({})` would hand the
  // compiler the literal "[object Object]" — the silent garbage that helper
  // refuses to persist — and an object with a non-callable `toString` would
  // throw right back into the swallowed-error path this is fixing.
  if (!Array.isArray(value)) {
    return typeof value === 'string' || typeof value === 'number'
      ? String(value)
      : '';
  }
  // Same filter for the ELEMENTS: an envelope can carry
  // `[{ "toString": 1 }]`, which `String(v)` cannot convert — it throws
  // `Cannot convert object to primitive value`, and `validate()` swallows that
  // into a `console.error`, silently stopping PIF validation. The array itself
  // is left alone by `coerceRowArrayColumns` (it IS an array), so this is the
  // only place that sees such an element.
  return value
    .map((v) =>
      typeof v === 'string' || typeof v === 'number' ? String(v) : ''
    )
    .join(delimiter);
};

/**
 * Defensive guard: the rest-spread in `prepareRowsDataForPifCompiler` will pass
 * through any TableNode field not explicitly destructured. If a new array-shaped
 * field is added to TableNode, it would silently reach the compiler as a raw
 * array instead of a delimited string. Throw loudly so the developer is forced
 * to add an explicit `joinDelimitedField` call.
 */
export const assertNoUntransformedArrays = (
  rest: Record<string, unknown>
): void => {
  for (const [key, value] of Object.entries(rest)) {
    if (Array.isArray(value)) {
      throw new Error(
        `prepareRowsDataForPifCompiler: array-shaped field "${key}" reached the rest-spread without explicit transformation. Destructure it and serialize via joinDelimitedField.`
      );
    }
  }
};

/**
 * The value the compiler payload will carry for a row's `Node #` cell.
 *
 * Exported because it is the ONLY definition of that mapping, and PSTUDIO-4120
 * added a second reader: the store has to know, before it calls the compiler,
 * whether the payload it is about to send contains a node the PIF grammar
 * cannot parse (`isCompilableNodeNumber` in `./validation-outcome`). Deriving
 * that from a second, hand-copied ternary is how the two would drift.
 *
 * `?? null` on the passthrough leg — see the call site in
 * `prepareRowsDataForPifCompiler` for why an `undefined` may not survive here.
 */
export const toCompilerNodeNumber = (value: unknown): unknown => {
  // An empty cell is 0, as `Number(null)` / `Number('')` always made it.
  if (value === null || value === '') return 0;
  if (typeof value === 'number') return value;
  // PSTUDIO-4453: a row holds a canonical string, or the text as typed. Only
  // the first is a number; "1e3" or "0x1F" passes through verbatim so the pass
  // refuses it instead of publishing node 1000 or 31.
  const canonical = canonicalNodeNumber(value);
  return canonical !== undefined ? Number(canonical) : (value ?? null);
};

export const isEmptyValue = (value: unknown): boolean => {
  if (value === null || value === undefined) return true;
  if (value === '') return true;
  if (Array.isArray(value) && value.length === 0) return true;
  if (
    typeof value === 'object' &&
    !Array.isArray(value) &&
    Object.keys(value as object).length === 0
  )
    return true;
  return false;
};

/**
 * Does the payload drop this field for a node of this type? The one definition
 * `stripNodeForPifCompiler` applies, exported so a reader that has to predict
 * the payload from a row (`isCompilableParameterInput`, PSTUDIO-4417) cannot
 * drift from it. Required keys are never dropped and are not asked about here.
 */
export const isPayloadFieldDroppedForNodeType = (
  key: string,
  nodeType: unknown
): boolean => {
  const normalizedType = String(nodeType).toUpperCase();
  return (
    (normalizedType === 'D' && ACTION_ONLY_PROPERTIES.includes(key)) ||
    (normalizedType === 'A' && DECISION_ONLY_PROPERTIES.includes(key))
  );
};

export const stripNodeForPifCompiler = (
  obj: Record<string, unknown>,
  nodeType: string
): Record<string, unknown> => {
  const result: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(obj)) {
    if (REQUIRED_NODE_KEYS.includes(key)) {
      result[key] = value;
      continue;
    }

    if (isPayloadFieldDroppedForNodeType(key, nodeType)) continue;
    if (isEmptyValue(value)) continue;

    if (
      key === 'richAsset' &&
      typeof value === 'object' &&
      value !== null &&
      !Array.isArray(value) &&
      (value as Record<string, unknown>).type === 'none' &&
      !(value as Record<string, unknown>).content
    ) {
      continue;
    }

    result[key] = value;
  }

  return result;
};

const normalizeTableData = (
  data: TableNode[],
  order = columnOrder,
  defaults = defaultRowData
): TableNode[] => {
  return data.map((row) => {
    const normalized: TableNode = {} as TableNode;

    for (const key of order) {
      if (key in row) {
        // @ts-ignore
        normalized[key] = row[key];
      } else if (key in defaults) {
        // @ts-ignore
        normalized[key] = defaults[key];
      } else {
        // @ts-ignore
        normalized[key] = null;
      }
    }

    return normalized;
  });
};

const customJSONStringify = (data: unknown[]): string => {
  const raw = JSON.stringify(data, null, 0);

  const arr = JSON.parse(raw);

  const formattedObjects = arr.map((obj: Record<string, unknown>) => {
    return (
      '  ' +
      JSON.stringify(obj, null, 0).replace(/^{/, '{ ').replace(/}$/, ' }')
    );
  });

  return `[\n${formattedObjects.join(',\n')}\n]`;
};

/**
 * PSTUDIO-3060: agent-compiler grammar (see "Validation & Compiler Checks" /
 * `parameterInput`) declares the field as an optional JSON value whose top-
 * level element must be an object. Author confirmed (2026-05-25):
 *   > Both null and "" value is acceptable for representing no value.
 *   > Or simply don't include the parameterInput field in case of no value
 *   > (which is better as it removes extra characters and shortens the
 *   > size of document).
 *
 * The table-store still holds the value as either an object (new cell editor)
 * or a JSON-encoded string (legacy CSV / API import). Normalize here, before
 * the value is serialized into the compiler payload:
 *   - non-string (object / array / null / undefined) → returned unchanged;
 *     `stripNodeForPifCompiler` drops null / undefined / {} via `isEmptyValue`
 *     so the field is simply omitted (preferred per the author's note).
 *   - empty string → returned unchanged; same `isEmptyValue` path drops it.
 *   - JSON-parseable string → parsed into the underlying JSON value
 *     (object / array / number / boolean / null). For top-level non-objects
 *     the compiler grammar will reject with Parser Error — we do not
 *     re-validate here; that is the compiler/validator's territory. The
 *     special case `JSON.parse('null') === null` collapses into the
 *     "no value → omit" path via `isEmptyValue`, matching the author's
 *     `null` ≡ omitted equivalence.
 *   - non-parseable string → returned unchanged so the compiler/validator
 *     surfaces a Parser Error to the user; silently dropping malformed
 *     content would hide the bug in the cell.
 */
export const coerceParameterInputForCompiler = (value: unknown): unknown => {
  if (typeof value !== 'string') return value;
  if (value === '') return value;

  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

/**
 * Serializes `TableNode[]` into the JSON string format consumed by
 * `AgentCompiler.validateJson` (and the legacy pif-compiler validator).
 *
 * Contract — every transformation here is part of the Bot Compiler payload
 * boundary (PSTUDIO-2220):
 *
 *  - **Group rows (nodeType='G') are stripped at function entry.** Defense
 *    in depth — every CALLER in `table-store.ts` also filters via
 *    `excludeGroupNodes` before reaching us, but a future direct caller
 *    must also be safe. See `./exclude-group-nodes.ts` for the rationale.
 *  - **`settings` is never serialized.** `normalizeTableData` walks
 *    `columnOrder`, which intentionally omits `settings`. Group metadata
 *    (`groupId`, `subRows`, `groupExpanded`, `groupName`) therefore cannot
 *    leak into the compiler payload even if a row somehow slipped past the
 *    G-filter above.
 *  - **`nodeType` is upper-cased** to match the compiler's expected enum
 *    ('A' | 'D'), and decision/action-only fields are pruned by
 *    `stripNodeForPifCompiler`.
 *  - **`parameterInput` is coerced** from its stored form (object or
 *    JSON-encoded string) into a parsed JSON value via
 *    `coerceParameterInputForCompiler` (PSTUDIO-3060).
 *
 * The output is a `customJSONStringify`'d array of node dictionaries — one
 * per non-group row, ready to be passed to `AgentCompiler.validateJson`.
 */
export const prepareRowsDataForPifCompiler = (data: TableNode[]) => {
  const compilerRows = excludeGroupNodes(data);

  const tableRows = compilerRows.map(
    ({
      nodeNumber,
      nodeType,
      output,
      variables,
      whatsNext,
      nextNodes,
      behaviors,
      utterances,
      ...rest
    }) => {
      assertNoUntransformedArrays(rest);
      return {
        // PSTUDIO-4120: `?? null` on the passthrough leg. A blank `Node #` cell
        // commits as `undefined` (`parseNativeCellRaw`), and `undefined` is the
        // one value `JSON.stringify` ERASES rather than serializes — so the node
        // left `customJSONStringify` with no `nodeNumber` field at all, both in
        // the compiler payload and in the PIF panel the user reads it from.
        // A `null` keeps the cleared number visible as a cleared number.
        //
        // What this does NOT do, stated because it is the tempting reading: it
        // does not make the pass succeed. The grammar's `NodeNumber` fragment
        // accepts bare `INTEGERS` only (`node.langium`), so the `null` THIS LINE
        // writes is still a parse error — it leaves `node.nodeNumber` undefined
        // in the AST and dies on the compiler's unguarded `.trim()`, exactly as
        // the missing key did. Verified by running agent-compiler 1.11.0 against
        // a missing key, a `null` and an `""` alike. Surviving that pass is
        // `validate()`'s job (`table-store.ts`), not this builder's.
        //
        // Note the asymmetry with a `null` that was already IN the row (a group
        // header, or a blank read back from the database): `Number(null)` is 0,
        // so the ternary above sends it down the finite branch and the payload
        // carries a parseable `0`. Only the `undefined` cell reaches `?? null`.
        //
        // A non-numeric STRING is still passed through verbatim: PSTUDIO-3007
        // put the reporting duty on the validator, so this path never cleans up
        // bad input (`.claude/rules/cross-cutting-invariants.md`, array-column
        // SHAPE row).
        nodeNumber: toCompilerNodeNumber(nodeNumber),
        nodeType: nodeType.toUpperCase() as Tenancy_Node_Type_Enum_Ps_Enum,
        output: joinDelimitedField(output, ','),
        whatsNext: joinDelimitedField(whatsNext, '|'),
        nextNodes: joinDelimitedField(nextNodes, ','),
        variables: joinDelimitedField(variables, ','),
        behaviors: joinDelimitedField(behaviors, ','),
        utterances: typeof utterances === 'string' ? utterances : '',
        ...rest,
        // When free text is closed (Disable Free Text), NLU never runs, so a node
        // can route to multiple children with "NLU Disabled" set without it being
        // a real contradiction. Force nluDisabled to false here so the PIF
        // compiler's single-child validation for NLU-disabled nodes can never
        // fire for such nodes — including legacy/imported data (behaviors is
        // normalized to an array on load). EXCEPT file_upload nodes: the compiler
        // requires nluDisabled=true whenever file_upload is specified, so those
        // are forced true instead. See PSTUDIO-2425.
        ...(Array.isArray(behaviors) && behaviors.includes('disable_input')
          ? {
              nluDisabled:
                (rest as { richAssetType?: string }).richAssetType ===
                Tenancy_Rich_Asset_Type_Enum_Ps_Enum.FileUpload,
            }
          : {}),
      };
    }
  );

  const dataForCompiling = customJSONStringify(
    // @ts-ignore
    normalizeTableData(tableRows, columnOrder, defaultRowData)
      .map(
        ({
          richAssetType,
          richAssetContent,
          cssClassname,
          analyticsEvent,
          ...rest
        }) => {
          const base = { ...rest, cssClassName: cssClassname };

          if (richAssetContent === undefined || richAssetContent === null) {
            return base;
          }

          const needsJsonParse =
            tableStore.isJsonAssetType(richAssetType) ||
            typeof richAssetContent === 'object';

          let content: unknown;
          if (needsJsonParse) {
            try {
              content = JSON.parse(`${richAssetContent}`);
            } catch {
              content = richAssetContent;
            }
          } else {
            content = richAssetContent;
          }

          return {
            ...base,
            richAsset: { type: richAssetType, content },
          };
        }
      )
      .map((item) => {
        const next = { ...(item as Record<string, unknown>) };
        if ('parameterInput' in next) {
          next.parameterInput = coerceParameterInputForCompiler(
            next.parameterInput
          );
        }
        return next;
      })
      .map((item) =>
        escapeJsonValues(item, [
          NodeColumns.whatsNext,
          'richAsset', // transformed from richAssetContent; exclude entire nested { type, content }
          'parameterInput', // PSTUDIO-3060: normalized to a parsed JSON value above; exclude so escapeJsonValues does not recurse into inner string values
        ])
      )
      .map((item) =>
        stripNodeForPifCompiler(
          item as Record<string, unknown>,
          (item as Record<string, unknown>).nodeType as string
        )
      )
  );

  return dataForCompiling;
};
